import spinnerSvg from './spinner.png';
import {
  useEffect,
  useRef,
  useState,
  Component,
  type ReactNode,
  type RefObject,
} from 'react';
import { formatDistance } from 'date-fns';
import { track } from '@/shared/utils/track';
import { captureException } from '@/modules/capture-exception';
import { notifyApiError } from '@/modules/notify-api-error';
import { useNow } from '@/modules/use-now';
import { useStateReducer } from '@/modules/use-state-reducer';
import { sleep } from '@/modules/sleep';
import { formatFixed2 } from '@/modules/format';
import { pnlPct } from '@/modules/market-forecasts';

import SpinnerCircle from '@/shared/ui/SpinnerCircle';
import SmartLink from '@/shared/ui/SmartLink';
import bell from './notification.svg';
import { useProfile } from '@/routes/Providers/ProfileProvider';
import upGraph from './up.png';
import downGraph from './down.png';
import type { Profile } from '@/routes/Providers/ProfileProvider';
import type { EndpointResponses } from '@/modules/sanr-api';

// The contract shape IS the component's data model — no local duplicate.
// null = not loaded yet (or reset on profile switch); loadedness is the
// value's nullness, never a separate flag.
type NotificationsValue = EndpointResponses['GET_NOTIFICATIONS'];
type NotificationItem = NotificationsValue['data'][number];

export default function DropdownContent({
  closeNotifications,
  dropdownContentWrRef,
  dropdownRef,
  isMoreLoading,
  markAllAsRead,
  markRead,
  notifications,
}: {
  closeNotifications: () => void;
  dropdownContentWrRef: RefObject<HTMLDivElement | null>;
  dropdownRef: RefObject<HTMLDivElement | null>;
  isMoreLoading: boolean;
  markAllAsRead: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  notifications: NotificationsValue | null;
}) {
  const now = useNow(60_000);
  const { profile } = useProfile();
  const [{ isMarkAllSubmitting }, ss] = useStateReducer({
    isMarkAllSubmitting: false,
  });

  // Rows marked read as they scroll into the list's viewport. The pending
  // ids render the row's spinner in place of its dot until the refetch
  // landed and a 600 ms settle passed; the ref mirrors the state so the
  // observer callback reads the live list without re-observing.
  const [readPendingIds, setReadPendingIds] = useState<string[]>([]);
  const readPendingRef = useRef<string[]>([]);
  const rowsRef = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    const setPending = (ids: string[]) => {
      readPendingRef.current = ids;
      setReadPendingIds(ids);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          void (async () => {
            const id =
              target instanceof HTMLElement ? target.dataset.id : undefined;
            if (id === undefined) return;
            try {
              const notification = notifications?.data.find((n) => n.id === id);
              if (
                isIntersecting &&
                !readPendingRef.current.includes(id) &&
                notification?.read !== true
              ) {
                setPending([...readPendingRef.current, id]);
                await markRead(id);
                await sleep(600);
                setPending(readPendingRef.current.filter((_id) => _id !== id));
              }
            } catch (e) {
              captureException(e);
              setPending(readPendingRef.current.filter((_id) => _id !== id));
            }
          })();
        });
      },
      { root: dropdownRef.current, threshold: 0.9 },
    );
    Object.values(rowsRef.current).forEach((row) => {
      if (row) observer.observe(row);
    });
    return () => observer.disconnect();
  }, [notifications, markRead, dropdownRef]);

  const handleMarkAllClick = () => {
    void (async () => {
      try {
        ss({ isMarkAllSubmitting: true });
        await markAllAsRead();
        track('notifications_marked_all_read');
        ss({ isMarkAllSubmitting: false });
      } catch (e) {
        ss({ isMarkAllSubmitting: false });
        captureException(e);
        notifyApiError(e);
      }
    })();
  };

  const markAllButton = notifications && notifications.unreadTotal > 0 && (
    <button
      type="button"
      className="ml-auto btn-reset w-fit text-body-sm font-regular text-blue-default disabled:cursor-not-allowed max-md:ml-l"
      disabled={isMarkAllSubmitting}
      onClick={handleMarkAllClick}
    >
      Mark all as read
    </button>
  );

  return (
    <div
      ref={dropdownContentWrRef}
      className="absolute right-[-20px] bottom-[-20px] flex h-112.5 w-103 translate-y-full flex-col overflow-hidden rounded-b-sm bg-background-secondary shadow-[0px_4px_10px_rgba(170,178,197,0.2)] max-md:relative max-md:right-auto max-md:bottom-auto max-md:h-auto max-md:max-h-none max-md:w-full max-md:translate-y-0 max-md:border-none max-md:shadow-none"
    >
      <div className="flex items-center px-xl pt-2xl pb-m text-body-lg font-bold text-gray-text max-md:mt-4xl max-md:ml-9 max-md:p-0">
        Notifications
        {markAllButton}
      </div>
      <div
        ref={dropdownRef}
        className="relative flex-1 overflow-y-auto overscroll-contain supports-[not_selector(::-webkit-scrollbar)]:scrollbar-thin max-md:max-h-[calc(100vh-64px-70px)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-xs [&::-webkit-scrollbar-thumb]:bg-(--btnSecondaryNormalBorder) [&::-webkit-scrollbar-track]:bg-transparent"
      >
        {(() => {
          if (!notifications) {
            return null;
          }
          if (notifications.data.length > 0) {
            return notifications.data.map(
              ({ id, date, read, details, type, fromWatchlist }) => (
                <div
                  key={id}
                  ref={(r) => {
                    rowsRef.current[id] = r;
                  }}
                  data-id={id}
                  className="relative border-b border-background-main px-2xl py-l pl-11.5 text-body-md text-gray-text max-md:px-9 max-md:py-2xl [&~&]:mt-xs"
                >
                  <div className="relative flex">
                    {readPendingIds.includes(id) ?
                      <div className="absolute top-1 -left-2xl flex h-3 w-3 shrink-0">
                        <SpinnerCircle size={12} />
                      </div>
                    : <ReadDot isntRead={!read} />}
                    <NotificationContentWr isntRead={!read}>
                      <NotificationContent
                        closeNotifications={closeNotifications}
                        details={details}
                        profile={profile}
                        type={type}
                      />
                    </NotificationContentWr>
                  </div>
                  <div className="mt-3.5 flex w-full items-baseline gap-s self-start text-body-sm whitespace-nowrap text-gray">
                    <div className="inline-flex self-start text-body-sm whitespace-nowrap text-gray">
                      {formatDistance(new Date(date), now, {
                        addSuffix: true,
                      })}
                    </div>
                    {fromWatchlist && (
                      <span className="inline-block before:mr-s before:content-['•']">
                        Watchlist
                      </span>
                    )}
                  </div>
                </div>
              ),
            );
          } else {
            return (
              <div className="mx-auto flex max-w-67.25 flex-col items-center justify-center text-center text-body-md text-gray">
                <div
                  className="mt-21.25 mb-13.5 h-13.25 w-12.5"
                  style={{
                    mask: `url(${JSON.stringify(bell)}) no-repeat center`,
                    maskSize: '50px 53px',
                    WebkitMask: `url(${JSON.stringify(bell)}) no-repeat center`,
                    WebkitMaskSize: '50px 53px',
                    backgroundColor: 'var(--gray-5)',
                  }}
                />
                Here you'll be notified about your predictions and activity from
                your watchlist.
              </div>
            );
          }
        })()}
      </div>
      {isMoreLoading ?
        <div className="flex items-center justify-center py-3xl pb-xl max-md:py-l">
          <div className="relative h-3xl w-3xl">
            <div
              className="absolute top-0.5 left-0.5 h-7 w-7 animate-spin bg-cover"
              style={{
                backgroundImage: `url(${JSON.stringify(spinnerSvg)})`,
                animationDuration: '0.6s',
              }}
            />
          </div>
        </div>
      : <button
          type="button"
          className="mt-auto btn-reset flex w-full items-center justify-center py-3xl pb-xl text-body-sm font-bold text-blue-default"
          onClick={closeNotifications}
        >
          Close
        </button>
      }
    </div>
  );
}

function ReadDot({ isntRead }: { isntRead: boolean }) {
  return (
    <div className="absolute top-1 -left-2xl h-3 w-3 shrink-0">
      {isntRead && (
        <div className="absolute top-0.5 left-0.5 h-2 w-2 rounded-full bg-blue-default" />
      )}
    </div>
  );
}

function NotificationContentWr({
  isntRead,
  children,
}: {
  isntRead: boolean;
  children: ReactNode;
}) {
  return (
    <div className="relative text-gray-text [&_b]:font-bold [&_span]:text-body-md">
      {isntRead && (
        <div className="absolute top-1.25 -left-5.5 h-2 w-2 rounded-full bg-blue-default" />
      )}
      {children}
    </div>
  );
}

type NotificationContentProps = {
  closeNotifications: () => void;
  type: string;
  details: NotificationItem['details'];
  profile: Profile | null;
};

class NotificationContent extends Component<NotificationContentProps> {
  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureException(error, info);
  }

  override render() {
    return <NotificationContentWrapped {...this.props} />;
  }
}

// The link's text is the translation's `<user>` content (Trans replaces the
// children), so the possessive stays inside the link where the sentence
// puts it.
function UserButton({
  username,
  closeNotifications,
  children,
}: {
  username: string;
  closeNotifications: () => void;
  children?: ReactNode;
}) {
  return (
    <SmartLink
      className="inline-block text-blue-default"
      onClick={() => {
        track('notification_clicked');
        closeNotifications();
      }}
      to="/user/$username"
      params={{ username }}
    >
      {children}
    </SmartLink>
  );
}

function NotificationContentWrapped({
  closeNotifications,
  type,
  details,
  profile,
}: NotificationContentProps) {
  const p = details.prediction;
  const issuerUsername = p?.issuer?.username ?? '';
  const symbol = p?.symbol ?? '';
  const leverage = p?.leverage ?? 1;
  const performance = p?.performance ?? null;
  const openPriceText = p?.openPrice == null ? '—' : formatFixed2(p.openPrice);
  const performancePct =
    performance == null ? null : pnlPct({ performance, leverage });
  const isUsersNotify = issuerUsername === profile?.username;
  const user = (
    <UserButton
      closeNotifications={closeNotifications}
      username={issuerUsername}
    >
      {issuerUsername}
    </UserButton>
  );
  const userPossessive = (
    <UserButton
      closeNotifications={closeNotifications}
      username={issuerUsername}
    >
      {issuerUsername}'s
    </UserButton>
  );
  const lvg = `(${symbol} lvg ${leverage}x)`;
  // A payload without a performance figure carries no performance line
  // rather than a fabricated 0%.
  const performanceLine =
    performancePct == null ? null : (
      <span className="whitespace-nowrap">
        <img
          className="inline-block h-3.75 w-3.5"
          src={performancePct > 0 ? upGraph : downGraph}
        />{' '}
        Performance is {formatFixed2(performancePct)}%
      </span>
    );
  if (type === 'user_opened_signal') {
    const isTriggered = Boolean(p?.openPriceIndex);
    return (
      <span>
        {isTriggered ?
          isUsersNotify ?
            `Your prediction has been opened upon reaching the trigger price ${openPriceText}$ ${lvg}`
          : <>
              {userPossessive} prediction has been opened upon reaching the
              trigger price {openPriceText}$ {lvg}
            </>

        : <>
            {user} opened a prediction {lvg}
          </>
        }
      </span>
    );
  }
  if (type === 'user_closed_signal') {
    return (
      <span>
        {user} closed a prediction {lvg} <br />
        {performanceLine}
      </span>
    );
  }
  if (type === 'user_auto_closed_signal') {
    return (
      <span>
        {isUsersNotify ?
          `Your prediction ${lvg} was automatically closed by 2w rule.`
        : <>
            {user} automatically closed a prediction {lvg} by 2w rule.
          </>
        }{' '}
        {performanceLine}
      </span>
    );
  }
  if (
    ['signal_take_profit_triggered', 'signal_stop_loss_triggered'].includes(
      type,
    )
  ) {
    const profit = type === 'signal_take_profit_triggered';
    return (
      <span>
        {isUsersNotify ?
          `Your prediction has reached the ${profit ? 'profit target' : 'stop-loss point'} ${lvg}.`
        : <>
            {userPossessive} prediction has reached the{' '}
            {profit ? 'profit target' : 'stop-loss point'} {lvg}.
          </>
        }{' '}
        {performancePct != null && (
          <span className="whitespace-nowrap">
            <img
              className="inline-block h-3.75 w-3.5"
              src={performancePct >= 0 ? upGraph : downGraph}
            />{' '}
            Performance is {formatFixed2(performancePct)}%
          </span>
        )}
      </span>
    );
  }
  return null;
}
